import store from 'store2';
import { put, takeLatest, select, debounce, call } from 'redux-saga/effects';

import i18n from '@mssgme/i18n';
import { history } from '@mssgme/services';

import { api, ui, checkout, setFeedbackItemsFilter, setAudienceFilter } from '../actions';
import { CHECKOUT_STATUSES } from '../constants';
import { getBillingRoot, tidyFilter } from './helpers';

/**
 * @yields {any}
 */
function* updateStatistics() {
    const state = yield select();
    const { status } = state.ui.conversationsFilter;
    yield put(api.conversations.statistics({ query: { status } }));
}

/**
 * @yields {any}
 */
function* handleFilterChange() {
    const state = yield select();
    yield call([store, 'set'], 'conversationsFilter', state.ui.conversationsFilter);
    yield put(api.conversations.filterList());
}

/**
 * @yields {any}
 */
function* updateUnread() {
    yield put(api.conversations.unread());
}

/**
 * @param {{ result: { website: string }}} action Action that initiated the update
 * @yields {any}
 */
function* updateRecentFeedback({ result }) {
    yield put(api.feedbacks.recent({ websiteId: result.website }));
}

/**
 * @yields {any}
 */
function* changeFilter() {
    yield put(
        ui.setConversationsFilter({
            assign: 'all',
            status: 'open',
            searchValue: '',
            showNewFirst: true,
        })
    );
}

function* getCollections() {
    yield put(api.collections.list());
}

function* getProducts(action) {
    yield put(api.products.list({ query: { website: action.payload.websiteId } }));
}

function* getWebsite(action) {
    yield put(api.websites.retrieve({ websiteId: action.websiteId }));
}

function* updateSentryContext() {
    const state = yield select();
    const { user } = state.currentUser;
    if (user) {
        window.Sentry.setUser({
            email: user.email,
            oid: user._id,
        });
    }
}

function* changeStatus(action) {
    // yield put(ui.setConversationsFilter({ status: action.status }));
    if (window.matchMedia('(max-width: 767px)').matches) {
        yield call(history.push, '/i');
    } else {
        const { conversations } = yield select();
        const items = conversations.list.ids
            .map((i) => conversations.list.byContactId[i])
            .filter((i) => i.status !== action.status && i.contact._id !== action.contactId);
        yield call(history.push, items.length > 0 ? `/i/${items[0].contact._id}` : '/i');
    }
}

function* fetchWorkspace() {
    yield put(api.workspaces.retrieve());
}

function* fetchConversation(action) {
    yield put(ui.setConversationsFilter({ status: 'open' }));
    yield put(api.conversations.retrieve({ contactId: action.contactId }));
}

function* fetchBilling() {
    yield put(api.workspaces.retrieve());
    yield put(api.billing.details());
}

function* completeCheckout() {
    const pathname = getBillingRoot();

    if (pathname) {
        yield call(history.push, `${pathname}/checkout-complete-successfully`);
    }
}

function* applyPlan() {
    const state = yield select();
    if (
        state.checkout.status === CHECKOUT_STATUSES.complete_payment_successfully &&
        state.billingDetails.result.subscription
    ) {
        yield put(checkout.applyPlan());
    }
}

function* completeChangePlan() {
    yield put(api.workspaces.retrieve());
    yield put(api.billing.details());

    const pathname = getBillingRoot();

    if (pathname) {
        yield call(history.push, pathname);
    }
}

/**
 * @yields {any}
 */
function* updateUiFlags() {
    const state = yield select();
    yield call([store, 'set'], 'advancedStylingMode', state.ui.isAdvancedStylingMode);
}

function* updateUser(action) {
    const state = yield select();
    yield put(api.users.updateMyAccount({ payload: { ...state.currentUser.user, country: action.payload.country } }));
}

function* updateLocale() {
    const { currentUser } = yield select();

    if (currentUser.user) {
        yield call([i18n, 'changeLanguage'], currentUser.user.lang);
    }
}

function* updateFeedbacks() {
    const { feedback: { filter } } = yield select();

    if (filter.website) {
        const { website, ...query } = filter;

        yield put(api.feedbacks.list({ websiteId: website, query: tidyFilter(query) }));
    }
}

function* updateAudience() {
    const { audience: { filter } } = yield select();

    if (filter.website) {
        const { website, ...query } = filter;

        yield put(api.websites.audience.list({ websiteId: website, query: tidyFilter(query) }));
    }
}

/**
 * @yields {any}
 */
export default function* rootSaga() {
    if (window.Sentry) {
        yield takeLatest(api.users.me.success, updateSentryContext);
    }
    yield debounce(
        100,
        [
            api.conversations.assign.success,
            api.conversations.assignMe.success,
            api.conversations.changeStatus.success,
            api.listenCreateConversation.type,
            api.listenUpdateConversation.type,
            ui.setConversationsFilter,
        ],
        updateStatistics
    );
    yield takeLatest(api.conversations.assignMe.success, changeFilter);
    yield takeLatest(ui.setConversationsFilter, handleFilterChange);
    yield takeLatest(ui.toggleStylingMode, updateUiFlags);
    yield takeLatest(
        [api.listenCreateConversation.type, api.listenUpdateConversation.type, api.workspaces.my],
        updateUnread
    );
    yield takeLatest(
        [
            api.feedbacks.update.success,
            api.feedbacks.delete.success,
            api.feedbacks.create.success,
            api.listenCreateFeedback.type,
            api.listenUpdateFeedback.type,
        ],
        updateRecentFeedback
    );
    yield takeLatest(
        [
            api.products.create.success,
            api.products.update.success,
            api.products.delete.success,
            api.products.migrate.success,
            api.websites.create.success,
            api.websites.scaffold.success,
            api.websites.delete.success,
            api.collections.products.update.success,
            api.blocks.convert.success,
        ],
        getCollections
    );
    yield takeLatest(api.products.migrate.success, getProducts);
    yield takeLatest([api.pages.create.success, api.pages.clone.success, api.pages.delete.success], getWebsite);
    yield debounce(
        300,
        [
            api.billing.update.success,
            api.billing.subscription.cancel.success,
            api.billing.changeCreditCard.success,
            api.listenSubscriptionCreated.type,
            api.listenSubscriptionUpdated.type,
            api.listenSubscriptionCancelled.type,
            api.listenPaymentSucceeded.type,
            api.listenPaymentFailed.type,
            api.listenPaymentRetryFailed.type,
            api.listenHighRiskTransactionUpdated.type,
            checkout.completePayment,
        ],
        fetchBilling
    );
    yield takeLatest(checkout.completePayment, completeCheckout);
    yield takeLatest(api.billing.changePlan.success, completeChangePlan);
    yield takeLatest(api.conversations.changeStatus.success, changeStatus);
    yield takeLatest(api.billing.details.success, applyPlan);
    yield takeLatest([api.integrations.create.success, api.integrations.delete.success], fetchWorkspace);
    yield takeLatest(
        [api.conversations.sendMessage.success, api.conversations.sendAttachment.success],
        fetchConversation
    );
    yield takeLatest(checkout.changeCountry, updateUser);
    if (process.env.REACT_APP_TARGET !== 'mobile') {
        yield takeLatest(api.users.me.success, updateLocale);
    }
    yield debounce(300, setFeedbackItemsFilter, updateFeedbacks);
    yield debounce(300, setAudienceFilter, updateAudience);
}
